/**
 * Push Notification Service for KELZIX
 * Handles browser push notifications and service worker registration
 */
import { apiClient } from './api';
import logger from '../utils/logger';

// VAPID public key - fetched from backend at runtime
let VAPID_PUBLIC_KEY = '';

/**
 * Fetch VAPID public key from backend
 */
async function fetchVapidKey(): Promise<string> {
  if (VAPID_PUBLIC_KEY) return VAPID_PUBLIC_KEY;

  try {
    const response = await apiClient.get('/notifications/vapid-key');
    VAPID_PUBLIC_KEY = response.data.publicKey || '';
  } catch (error) {
    // Silently fail
  }
  return VAPID_PUBLIC_KEY;
}

/**
 * Check if push notifications are supported
 */
export function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission(): NotificationPermission {
  return Notification.permission;
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isPushSupported()) {
    logger.warn('Push notifications not supported');
    return 'denied';
  }

  const permission = await Notification.requestPermission();
  logger.log('Notification permission:', permission);
  return permission;
}

/**
 * Register service worker
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    logger.warn('Service workers not supported');
    return null;
  }

  try {
    // Register service worker without blocking
    const registration = await navigator.serviceWorker.register('/service-worker.js', {
      scope: '/'
    });

    logger.log('Service Worker registered:', registration.scope);

    // Don't wait for ready - it can hang if there are issues
    // The service worker will be ready when needed
    return registration;
  } catch (error) {
    logger.error('Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Convert URL-safe base64 to Uint8Array for VAPID
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Subscribe to push notifications
 */
export async function subscribeToPush(registration: ServiceWorkerRegistration): Promise<PushSubscription | null> {
  try {
    // Fetch VAPID key from backend
    const vapidKey = await fetchVapidKey();

    // Check if already subscribed
    let subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      // Verify the existing subscription uses the current VAPID key
      // If the VAPID key changed, unsubscribe and re-subscribe
      if (vapidKey) {
        const currentKey = urlBase64ToUint8Array(vapidKey);
        const existingKey = subscription.options?.applicationServerKey
          ? new Uint8Array(subscription.options.applicationServerKey)
          : null;

        const keysMatch = existingKey &&
          existingKey.length === currentKey.length &&
          existingKey.every((val, i) => val === currentKey[i]);

        if (!keysMatch) {
          logger.log('VAPID key changed, re-subscribing...');
          await subscription.unsubscribe();
          subscription = null;
        } else {
          logger.log('Already subscribed with correct VAPID key');
          return subscription;
        }
      } else {
        return subscription;
      }
    }

    // Subscribe with VAPID key
    if (vapidKey) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
    } else {
      // Fallback without VAPID (for development)
      logger.warn('No VAPID key available, push notifications may not work');
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true
      });
    }

    logger.log('Push subscription successful:', subscription);
    return subscription;
  } catch (error) {
    logger.error('Push subscription failed:', error);
    return null;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();

    if (subscription) {
      await subscription.unsubscribe();
      logger.log('Unsubscribed from push notifications');
      return true;
    }
    return false;
  } catch (error) {
    logger.error('Unsubscribe failed:', error);
    return false;
  }
}

/**
 * Send push subscription to backend
 */
export async function sendSubscriptionToServer(subscription: PushSubscription): Promise<boolean> {
  try {
    await apiClient.post('/notifications/subscribe', {
      subscription: subscription.toJSON()
    });
    return true;
  } catch (error) {
    return false;
  }
}

/**
 * Show a local notification (for testing or when app is in foreground)
 */
export async function showLocalNotification(
  title: string,
  options: NotificationOptions = {}
): Promise<void> {
  if (Notification.permission !== 'granted') {
    logger.warn('Notification permission not granted');
    return;
  }

  const registration = await navigator.serviceWorker.ready;

  const defaultOptions: NotificationOptions = {
    icon: '/logo192.png',
    badge: '/logo192.png',
    vibrate: [200, 100, 200],
    tag: 'kelzix-notification',
    ...options
  };

  await registration.showNotification(title, defaultOptions);
}

/**
 * Initialize push notifications
 * Call this when user logs in or enables notifications
 */
export async function initializePushNotifications(): Promise<{
  supported: boolean;
  permission: NotificationPermission;
  subscription: PushSubscription | null;
}> {
  const result = {
    supported: isPushSupported(),
    permission: 'default' as NotificationPermission,
    subscription: null as PushSubscription | null
  };

  if (!result.supported) {
    return result;
  }

  // Register service worker
  const registration = await registerServiceWorker();
  if (!registration) {
    return result;
  }

  // Check permission
  result.permission = getNotificationPermission();

  // If already granted, get subscription
  if (result.permission === 'granted') {
    result.subscription = await subscribeToPush(registration);

    // Send subscription to backend
    if (result.subscription) {
      await sendSubscriptionToServer(result.subscription);
    }
  }

  return result;
}

/**
 * Enable push notifications (request permission and subscribe)
 */
export async function enablePushNotifications(): Promise<{
  success: boolean;
  permission: NotificationPermission;
  subscription: PushSubscription | null;
}> {
  const result = {
    success: false,
    permission: 'default' as NotificationPermission,
    subscription: null as PushSubscription | null
  };

  if (!isPushSupported()) {
    logger.warn('Push notifications not supported');
    return result;
  }

  // Request permission
  result.permission = await requestNotificationPermission();

  if (result.permission !== 'granted') {
    return result;
  }

  // Register service worker
  const registration = await registerServiceWorker();
  if (!registration) {
    return result;
  }

  // Subscribe to push
  result.subscription = await subscribeToPush(registration);

  if (result.subscription) {
    // Send to backend
    const sent = await sendSubscriptionToServer(result.subscription);
    result.success = sent;
  }

  return result;
}

/**
 * Check if push notifications are enabled
 */
export async function isPushEnabled(): Promise<boolean> {
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'granted') return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}
